import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Input,
  VStack,
  HStack,
  Text,
  Avatar,
  InputGroup,
  InputLeftElement,
  Spinner,
  useColorModeValue,
  Flex,
  Badge,
  Button,
  IconButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Divider,
  Skeleton,
} from '@chakra-ui/react';
import { FiSearch, FiUser, FiMail, FiPhone, FiX, FiCheck } from 'react-icons/fi';

export interface User {
  id: string;
  name?: string;
  surname?: string;
  email?: string;
  phone_number?: string;
  role?: string;
  photo?: string;
}

interface UserPickerProps {
  users: User[];
  value?: User | null;
  onChange: (user: User | null) => void;
  placeholder?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  maxResults?: number;
  label?: string;
  isRequired?: boolean;
}

const UserPicker: React.FC<UserPickerProps> = ({
  users,
  value,
  onChange,
  placeholder = "Search for users...",
  isLoading = false,
  isDisabled = false,
  error,
  size = 'md',
  maxResults = 10,
  label,
  isRequired = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const selectedBorderColor = useColorModeValue('blue.200', 'blue.600');

  // Filter users based on search query
  const filteredUsers = React.useMemo(() => {
    if (!searchQuery.trim()) return users.slice(0, maxResults);
    
    const query = searchQuery.toLowerCase();
    return users
      .filter(user => {
        const fullName = `${user.name || ''} ${user.surname || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phoneNumber = (user.phone_number || '').toLowerCase();
        return fullName.includes(query) || email.includes(query) || phoneNumber.includes(query);
      })
      .slice(0, maxResults);
  }, [users, searchQuery, maxResults]);

  // Handle input focus
  const handleInputFocus = () => {
    if (!isDisabled && !value) {
      setShowDropdown(true);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  // Handle user selection
  const handleUserSelect = (user: User) => {
    onChange(user);
    setSearchQuery('');
    setShowDropdown(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle clear selection
  const handleClearSelection = () => {
    onChange(null);
    setSearchQuery('');
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredUsers[highlightedIndex]) {
          handleUserSelect(filteredUsers[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format user display name
  const getUserDisplayName = (user: User): string => {
    const fullName = `${user.name || ''} ${user.surname || ''}`.trim();
    return fullName || user.email || user.phone_number || 'Unknown User';
  };

  // Format user subtitle
  const getUserSubtitle = (user: User): string => {
    const parts = [];
    if (user.email) parts.push(user.email);
    if (user.phone_number) parts.push(user.phone_number);
    return parts.join(' • ');
  };

  const renderSelectedUser = () => {
    if (!value) return null;

    return (
      <Box
        p={3}
        borderWidth="2px"
        borderRadius="md"
        borderColor={selectedBorderColor}
        bg={selectedBg}
        position="relative"
      >
        <HStack spacing={3} justify="space-between">
          <HStack spacing={3} flex={1}>
            <Avatar
              size="sm"
              name={getUserDisplayName(value)}
              src={value.photo}
              bg="blue.500"
            />
            <VStack align="start" spacing={0} flex={1}>
              <HStack spacing={2}>
                <Text fontWeight="medium" fontSize="sm">
                  {getUserDisplayName(value)}
                </Text>
                {value.role && (
                  <Badge size="xs" colorScheme="blue" variant="subtle">
                    {value.role}
                  </Badge>
                )}
              </HStack>
              {getUserSubtitle(value) && (
                <Text fontSize="xs" color="gray.600">
                  {getUserSubtitle(value)}
                </Text>
              )}
            </VStack>
            <IconButton
              aria-label="Selected"
              icon={<FiCheck />}
              size="sm"
              colorScheme="green"
              variant="ghost"
              isDisabled
            />
          </HStack>
          <IconButton
            aria-label="Clear selection"
            icon={<FiX />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={handleClearSelection}
            isDisabled={isDisabled}
          />
        </HStack>
      </Box>
    );
  };

  const renderSearchInput = () => (
    <Box position="relative" ref={dropdownRef}>
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none">
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <FiSearch color="gray.400" />
          )}
        </InputLeftElement>
        <Input
          ref={inputRef}
          placeholder={value ? "Search to change user..." : placeholder}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          isDisabled={isDisabled || isLoading}
          bg={bg}
          isInvalid={!!error}
        />
      </InputGroup>

      {/* Dropdown */}
      {showDropdown && !isLoading && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg={bg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow={`0 4px 12px ${shadowColor}`}
          maxHeight="300px"
          overflowY="auto"
          mt={1}
        >
          {filteredUsers.length === 0 ? (
            <Box p={4} textAlign="center">
              <Text color="gray.500" fontSize="sm">
                {searchQuery ? `No users found for "${searchQuery}"` : 'No users available'}
              </Text>
            </Box>
          ) : (
            filteredUsers.map((user, index) => (
              <Box
                key={user.id}
                p={3}
                cursor="pointer"
                bg={index === highlightedIndex ? hoverBg : 'transparent'}
                _hover={{ bg: hoverBg }}
                onClick={() => handleUserSelect(user)}
                borderBottomWidth={index < filteredUsers.length - 1 ? "1px" : "0"}
                borderBottomColor={borderColor}
              >
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={getUserDisplayName(user)}
                    src={user.photo}
                    bg="blue.500"
                  />
                  <VStack align="start" spacing={0} flex={1}>
                    <HStack spacing={2}>
                      <Text fontWeight="medium" fontSize="sm">
                        {getUserDisplayName(user)}
                      </Text>
                      {user.role && (
                        <Badge size="xs" colorScheme="gray" variant="subtle">
                          {user.role}
                        </Badge>
                      )}
                    </HStack>
                    <HStack spacing={4} fontSize="xs" color="gray.600">
                      {user.email && (
                        <HStack spacing={1}>
                          <FiMail />
                          <Text>{user.email}</Text>
                        </HStack>
                      )}
                      {user.phone_number && (
                        <HStack spacing={1}>
                          <FiPhone />
                          <Text>{user.phone_number}</Text>
                        </HStack>
                      )}
                    </HStack>
                  </VStack>
                </HStack>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );

  if (isLoading && !users.length) {
    return (
      <FormControl isInvalid={!!error} isRequired={isRequired}>
        {label && <FormLabel>{label}</FormLabel>}
        <VStack spacing={3}>
          <Skeleton height="40px" width="100%" />
          <Text fontSize="sm" color="gray.500">Loading users...</Text>
        </VStack>
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>
    );
  }

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      {label && <FormLabel>{label}</FormLabel>}
      <VStack spacing={3} align="stretch">
        {value ? renderSelectedUser() : renderSearchInput()}
        
        {!value && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<FiSearch />}
            onClick={() => {
              setShowDropdown(true);
              inputRef.current?.focus();
            }}
            isDisabled={isDisabled || isLoading}
          >
            Browse Users
          </Button>
        )}
      </VStack>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

export default UserPicker;